import React, { useState } from 'react';
import styled from 'styled-components';
import { PageContainer } from '@/components/layout';
import { useAuth } from '@/context';
import { GeneralSchoolSection } from '@features/settings/components/GeneralSchoolSection';
import { OrganisationTaxSection } from '@features/settings/components/OrganisationTaxSection';
import { NotificationsSection } from '@features/settings/components/NotificationsSection';

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active, theme }) => $active ? theme.colors.primaryLight : 'transparent'};
  text-align: left;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SettingsContent = styled.div``;

const settingsSections = [
  { id: 'general', label: 'General', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg> },
  { id: 'booking', label: 'Booking Policies', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg> },
  { id: 'billing', label: 'Billing', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> },
  { id: 'notifications', label: 'Notifications', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg> },
  { id: 'branches', label: 'Branches', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg> },
  // 11E.6 — tenant-wide annual leave allocation. Rendered via render-prop
  // (see SettingsProps below) — Settings must not import from Attendance
  // directly (ESLint feature-boundary rule, docs/architecture/code-boundaries.md).
  { id: 'leaves', label: 'Leave Policy', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><path d="M8 2v4M16 2v4M3 10h18" /><path d="M9 16l2 2 4-4" /></svg> },
];

// 11F.8 — owner-only, appended conditionally (see settingsSectionsFor below):
// non-owners must never even see the nav item, matching the 11F.3 pattern.
const organisationTaxNavItem = { id: 'organisation', label: 'Organisation & Tax', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M15 3v18M3 9h18M3 15h18" /></svg> };

// 11F.3 — owner-only, appended conditionally (see settingsSectionsFor below)
// rather than listed statically: non-owners must never even see the nav
// item, matching the "non-owner never sees the switcher" pattern from 11E.5.
const ownerAccountsSection = { id: 'owners', label: 'Owner Accounts', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg> };

function settingsSectionsFor(isOwner: boolean) {
  if (!isOwner) return settingsSections;
  const [general, ...rest] = settingsSections;
  return [general, organisationTaxNavItem, ...rest, ownerAccountsSection];
}

interface SettingsProps {
  /** Injected from Attendance at the route layer — see src/routes/index.tsx. */
  renderLeavePolicySection?: () => React.ReactNode;
  /** Injected from Auth at the route layer — see src/routes/index.tsx. */
  renderOwnerAccountsSection?: () => React.ReactNode;
  /** Injected from Bookings at the route layer — see src/routes/index.tsx. */
  renderBookingPolicySection?: () => React.ReactNode;
  /** Injected from Invoices at the route layer — see src/routes/index.tsx. */
  renderBillingPolicySection?: () => React.ReactNode;
  /** Injected from Calendar at the route layer — see src/routes/index.tsx. */
  renderBranchesSection?: () => React.ReactNode;
}

export const Settings: React.FC<SettingsProps> = ({
  renderLeavePolicySection,
  renderOwnerAccountsSection,
  renderBookingPolicySection,
  renderBillingPolicySection,
  renderBranchesSection,
}) => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [activeSection, setActiveSection] = useState('general');

  return (
    <PageContainer title="Settings" subtitle="Configure your Candeza workspace">
      <SettingsGrid>
        <SettingsNav>
          {settingsSectionsFor(isOwner).map(section => (
            <NavItem
              key={section.id}
              $active={activeSection === section.id}
              onClick={() => setActiveSection(section.id)}
            >
              {section.icon}
              {section.label}
            </NavItem>
          ))}
        </SettingsNav>

        <SettingsContent>
          {activeSection === 'general' && <GeneralSchoolSection />}

          {activeSection === 'organisation' && isOwner && <OrganisationTaxSection />}

          {activeSection === 'booking' &&
            (renderBookingPolicySection ? (
              renderBookingPolicySection()
            ) : (
              <p style={{ color: '#64748b' }}>Booking policy settings are not wired.</p>
            ))}

          {activeSection === 'billing' &&
            (renderBillingPolicySection ? (
              renderBillingPolicySection()
            ) : (
              <p style={{ color: '#64748b' }}>Billing policy settings are not wired.</p>
            ))}

          {activeSection === 'notifications' && <NotificationsSection />}

          {activeSection === 'branches' &&
            (renderBranchesSection ? (
              renderBranchesSection()
            ) : (
              <p style={{ color: '#64748b' }}>Branch management is not wired.</p>
            ))}

          {activeSection === 'leaves' &&
            (renderLeavePolicySection ? (
              renderLeavePolicySection()
            ) : (
              <p style={{ color: '#64748b' }}>Leave policy settings are not wired.</p>
            ))}

          {activeSection === 'owners' &&
            isOwner &&
            (renderOwnerAccountsSection ? (
              renderOwnerAccountsSection()
            ) : (
              <p style={{ color: '#64748b' }}>Owner account provisioning is not wired.</p>
            ))}
        </SettingsContent>
      </SettingsGrid>
    </PageContainer>
  );
};
